import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  SqlImportPreviewResult,
  SqlImportsImportAttributes,
  SqlImportsImportReply,
  SqlImportsPreviewRequest,
  SqlImportsUpdateQueryAttributes,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { invalidateSqlImportLists, removeSqlImportCache } from './cache';
import { parseSqlImportReply, populateSqlImportReply } from './populate';

/**
 * The reply every single-import write returns: the import plus its database,
 * with the draft placeholder database already read as unnamed. The hooks write
 * both into the cache (`populateSqlImportReply`); callers read the saved import
 * off `reply.sql_import`.
 */
export type SqlImportReply = SqlImportsImportReply;

/**
 * The create/update body — the generated `sql_import` request object, so the
 * write surface cannot drift from the spec (snake_case, per the wire). `name`
 * and `import_type` are required by the schema; a create may omit `database_id`
 * to start from a draft database, `database_type` is required on create only,
 * and `import_status: 'published'` is what activates a draft.
 */
export type SqlImportWriteInput = SqlImportsImportAttributes;

/** The `update_query` body: the query, its BigQuery location, and a relationship import's own query. */
export type SqlImportQueryWriteInput = SqlImportsUpdateQueryAttributes;

/**
 * The preview body. `import_type` picks which identifier fields the backend
 * reads: `object_type_id` for objects, the `entity*` fields for relationships.
 */
export type SqlImportPreviewInput = SqlImportsPreviewRequest;

/**
 * What a new import starts as when the caller does not say otherwise: starting
 * on activation, deleting an attribute when its cell is empty (`clear_empty_cells`
 * is the "ignore empty cells" choice, off by default), marking suppressed people
 * successful, saved as a draft. These are the model defaults the Ember wizard
 * sent on every create; the backend only defaults `id_field_name`, so they are
 * set here rather than left to the server.
 */
const SQL_IMPORT_CREATE_DEFAULTS = {
  start_at: 0,
  clear_empty_cells: false,
  ignore_suppress_err: true,
  import_status: 'draft',
} as const satisfies Partial<SqlImportWriteInput>;

/* ------------------------------------------------------------------------- */
/* Shared hook shape                                                          */
/* ------------------------------------------------------------------------- */

interface UseSqlImportReplyMutationOptions<TVariables> {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<SqlImportReply, TVariables>;
}

/**
 * Every single-import write answers with the import and its database. The hook
 * caches both (`populateSqlImportReply`) and, unless the write cannot change
 * what the list shows (`invalidateLists: false`), refreshes the lists — a
 * write can change `active`, `name` or `last_synced_at`, all of which the list
 * groups or renders.
 */
function useSqlImportReplyMutation<TVariables>(
  run: (workspaceId: number, variables: TVariables) => Promise<SqlImportReply>,
  { workspaceId: overrideWorkspaceId, mutationConfig }: UseSqlImportReplyMutationOptions<TVariables>,
  { invalidateLists = true }: { invalidateLists?: boolean } = {},
) {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: TVariables) => run(workspaceId, variables),
    onSuccess: (reply, ...callbackArgs) => {
      populateSqlImportReply(qc, workspaceId, reply);
      if (invalidateLists) {
        invalidateSqlImportLists(qc, workspaceId);
      }
      onSuccess?.(reply, ...callbackArgs);
    },
  });
}

/* ------------------------------------------------------------------------- */
/* createSqlImport                                                            */
/* ------------------------------------------------------------------------- */

export interface CreateSqlImportInput extends SqlImportWriteInput {
  workspaceId: number;
}

/** @public */
export async function createSqlImport({ workspaceId, ...write }: CreateSqlImportInput): Promise<SqlImportReply> {
  const data = await hydraUiApi.sqlImports.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { sql_import: { ...SQL_IMPORT_CREATE_DEFAULTS, ...write } },
  });
  return parseSqlImportReply(data);
}

type UseCreateSqlImportVariables = Omit<CreateSqlImportInput, 'workspaceId'>;

/** @public */
export const useCreateSqlImport = (options: UseSqlImportReplyMutationOptions<UseCreateSqlImportVariables> = {}) =>
  useSqlImportReplyMutation(
    (workspaceId, variables: UseCreateSqlImportVariables) => createSqlImport({ workspaceId, ...variables }),
    options,
  );

/* ------------------------------------------------------------------------- */
/* updateSqlImport                                                            */
/* ------------------------------------------------------------------------- */

export interface UpdateSqlImportInput extends SqlImportWriteInput {
  workspaceId: number;
  sqlImportId: string;
}

/**
 * Save an import's settings. An active import rejects a changed `database_id`
 * or `id_field_name`; a draft with `import_status: 'published'` is activated
 * and starts its first run.
 *
 * @public
 */
export async function updateSqlImport({
  workspaceId,
  sqlImportId,
  ...write
}: UpdateSqlImportInput): Promise<SqlImportReply> {
  const data = await hydraUiApi.sqlImports.update({
    params: { path: { environment_id: String(workspaceId), id: sqlImportId } },
    body: { sql_import: write },
  });
  return parseSqlImportReply(data);
}

type UseUpdateSqlImportVariables = Omit<UpdateSqlImportInput, 'workspaceId'>;

/** @public */
export const useUpdateSqlImport = (options: UseSqlImportReplyMutationOptions<UseUpdateSqlImportVariables> = {}) =>
  useSqlImportReplyMutation(
    (workspaceId, variables: UseUpdateSqlImportVariables) => updateSqlImport({ workspaceId, ...variables }),
    options,
  );

/* ------------------------------------------------------------------------- */
/* deleteSqlImport                                                            */
/* ------------------------------------------------------------------------- */

export interface DeleteSqlImportInput {
  workspaceId: number;
  sqlImportId: string;
}

/** @public */
export async function deleteSqlImport({ workspaceId, sqlImportId }: DeleteSqlImportInput): Promise<void> {
  await hydraUiApi.sqlImports.delete({
    params: { path: { environment_id: String(workspaceId), id: sqlImportId } },
  });
}

type UseDeleteSqlImportVariables = Omit<DeleteSqlImportInput, 'workspaceId'>;

interface UseDeleteSqlImportOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteSqlImportVariables>;
}

/** @public */
export const useDeleteSqlImport = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseDeleteSqlImportOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteSqlImportVariables) => deleteSqlImport({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      removeSqlImportCache(qc, workspaceId, variables.sqlImportId);
      invalidateSqlImportLists(qc, workspaceId);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* startSqlImport / stopSqlImport                                             */
/* ------------------------------------------------------------------------- */

export interface ToggleSqlImportInput {
  workspaceId: number;
  sqlImportId: string;
}

/** Activate a paused import; it resumes syncing on its interval. @public */
export async function startSqlImport({ workspaceId, sqlImportId }: ToggleSqlImportInput): Promise<SqlImportReply> {
  const data = await hydraUiApi.sqlImports.start({
    params: { path: { environment_id: String(workspaceId), id: sqlImportId } },
  });
  return parseSqlImportReply(data);
}

/** Pause an active import; runs already in progress finish. @public */
export async function stopSqlImport({ workspaceId, sqlImportId }: ToggleSqlImportInput): Promise<SqlImportReply> {
  const data = await hydraUiApi.sqlImports.stop({
    params: { path: { environment_id: String(workspaceId), id: sqlImportId } },
  });
  return parseSqlImportReply(data);
}

type UseToggleSqlImportVariables = Omit<ToggleSqlImportInput, 'workspaceId'>;

/** @public */
export const useStartSqlImport = (options: UseSqlImportReplyMutationOptions<UseToggleSqlImportVariables> = {}) =>
  useSqlImportReplyMutation(
    (workspaceId, variables: UseToggleSqlImportVariables) => startSqlImport({ workspaceId, ...variables }),
    options,
  );

/** @public */
export const useStopSqlImport = (options: UseSqlImportReplyMutationOptions<UseToggleSqlImportVariables> = {}) =>
  useSqlImportReplyMutation(
    (workspaceId, variables: UseToggleSqlImportVariables) => stopSqlImport({ workspaceId, ...variables }),
    options,
  );

/* ------------------------------------------------------------------------- */
/* updateSqlImportQuery                                                       */
/* ------------------------------------------------------------------------- */

export interface UpdateSqlImportQueryInput extends SqlImportQueryWriteInput {
  workspaceId: number;
  sqlImportId: string;
}

/**
 * Replace an import's query without touching its other settings. `relationship`
 * is only accepted on an import that already has one configured.
 *
 * @public
 */
export async function updateSqlImportQuery({
  workspaceId,
  sqlImportId,
  ...write
}: UpdateSqlImportQueryInput): Promise<SqlImportReply> {
  const data = await hydraUiApi.sqlImports.updateQuery({
    params: { path: { environment_id: String(workspaceId), id: sqlImportId } },
    body: { sql_import: write },
  });
  return parseSqlImportReply(data);
}

type UseUpdateSqlImportQueryVariables = Omit<UpdateSqlImportQueryInput, 'workspaceId'>;

/** @public The query is not shown in the list, so only the detail slot is refreshed. */
export const useUpdateSqlImportQuery = (
  options: UseSqlImportReplyMutationOptions<UseUpdateSqlImportQueryVariables> = {},
) =>
  useSqlImportReplyMutation(
    (workspaceId, variables: UseUpdateSqlImportQueryVariables) => updateSqlImportQuery({ workspaceId, ...variables }),
    options,
    { invalidateLists: false },
  );

/* ------------------------------------------------------------------------- */
/* previewSqlImport                                                           */
/* ------------------------------------------------------------------------- */

export interface PreviewSqlImportInput extends SqlImportPreviewInput {
  workspaceId: number;
}

/**
 * Run a query against a connected database and return sample rows plus the
 * validation findings (`validations.errors` block activation, `warnings` do
 * not). A query the database rejects, or that runs past the ~25s limit, comes
 * back as a 200 with `error` set — "The query timed out." for the latter —
 * rather than a failed request. Touches no cached state.
 *
 * @public
 */
export async function previewSqlImport({
  workspaceId,
  ...body
}: PreviewSqlImportInput): Promise<SqlImportPreviewResult> {
  return hydraUiApi.sqlImportsPreview.create({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

type UsePreviewSqlImportVariables = Omit<PreviewSqlImportInput, 'workspaceId'>;

interface UsePreviewSqlImportOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<SqlImportPreviewResult, UsePreviewSqlImportVariables>;
}

/** @public */
export const usePreviewSqlImport = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UsePreviewSqlImportOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useMutation({
    ...mutationConfig,
    mutationFn: (variables: UsePreviewSqlImportVariables) => previewSqlImport({ workspaceId, ...variables }),
  });
};
